import React, { useEffect, useState } from "react";
import { isValidArray } from "../../Layout/Common/common";
import "./orderStatusStepper.css";

// Seconds each step waits behind the previous one in the fill cascade.
// Dot k lights at k*STEP_DURATION; the connector after it fills over the
// next STEP_DURATION, so the following dot lights right as the stripe arrives.
const STEP_DURATION = 0.5;

/**
 * Horizontal order status stepper with a sequential fill animation.
 *
 * On mount everything starts grey/empty, then fills from the first step:
 * dot 0 lights, the dotted connector fills toward dot 1, dot 1 lights once
 * the stripe reaches it, and so on up to the last completed status. Pending
 * steps stay hollow grey with an "Exp:" date.
 *
 * @param {Object[]} steps                      one entry per step
 * @param {string}   steps[].statusDescription  label shown under the dot
 * @param {boolean}  steps[].isCompleted        true => green dot + plain date
 * @param {string}   steps[].completedDate      date for a completed step
 * @param {string}   steps[].expectedDate       expected date for a pending step
 */
export const OrderStatusStepper = ({ steps = [] }) => {
  const [started, setStarted] = useState(false);

  useEffect(() => {
    // Reset to the empty state, then trigger the cascade on the next frame so
    // the CSS transitions have a grey/zero-width starting point to animate from.
    setStarted(false);
    const id = requestAnimationFrame(() => setStarted(true));
    return () => cancelAnimationFrame(id);
  }, [steps]);

  // Nothing to show once loaded -> centered "No data found".
  if (!isValidArray(steps)) {
    return <div className="progress-tracker-card text-center">No data found</div>;
  }

  // The furthest completed step — its dot keeps pulsing to show "you are here".
  let lastCompletedIndex = -1;
  steps.forEach((step, index) => {
    if (step.isCompleted) lastCompletedIndex = index;
  });

  return (
    <div className={`progress-tracker-card order-status-stepper ${started ? "is-animating" : ""}`}>
      <div className="oss-track">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          const isCurrent = index === lastCompletedIndex;
          // A connector fills only when it links two completed steps, so the
          // green run stays continuous and stops after the last completed one.
          const connectorFilled = step.isCompleted && steps[index + 1]?.isCompleted;
          const date = step.isCompleted ? step.completedDate : step.expectedDate || step.completedDate;
          const delay = `${index * STEP_DURATION}s`;

          return (
            <div
              className={`oss-step ${step.isCompleted ? "completed" : ""} ${isCurrent ? "current" : ""}`}
              key={index}
            >
              <div className="oss-dot-row">
                <span className="oss-dot" style={{ transitionDelay: delay, "--oss-pulse-delay": delay }} />
                {!isLast && (
                  <span className={`oss-connector ${connectorFilled ? "fillable" : ""}`}>
                    <span className="oss-connector-fill" style={{ transitionDelay: delay }} />
                  </span>
                )}
              </div>
              <div className="progress-stepInfo">
                <div className="step-label">{step.statusDescription}</div>
                {date && <div className="step-date">{step.isCompleted ? date : `Exp: ${date}`}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
